import { MaestroAPI, maestroApiCall, useSnackbar, useTranslation } from '@michelin/central-provider';
import { useMutation } from '@tanstack/react-query';
import GLOBAL_CONFIG from 'global-variables';
import { ExtractMileageResponse, UploadMileageResponse } from '../modules/MileageDeclarations/types';

const baseURL = GLOBAL_CONFIG.externalAPIUrl;

// Extract mileage data by referencing uploaded file id
const extractMileage = async (fileID: string, fleetId: string | undefined): Promise<ExtractMileageResponse | null> => {
  if (!fileID || !fleetId) return null;

  const endpoint = `cp-be/file-uploads/${fleetId}/vehicle-parse-xlsx/${encodeURIComponent(fileID)}`;
  const apiType: MaestroAPI = {
    method: 'POST',
    endpoint,
    baseURL,
  };

  const res = await maestroApiCall<ExtractMileageResponse>(apiType);
  return res as ExtractMileageResponse;
};

export function useExtractMileage({ fleetId }: { fleetId: string | undefined }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<ExtractMileageResponse | null, Error, string>({
    mutationFn: (fileID: string) => extractMileage(fileID, fleetId),
    onError: () => {
      enqueueSnackbar(t('Extraction failed. Please try again.'), { variant: 'error' });
    },
  });
}

// Upload extracted mileage declarations
const uploadMileage = async (fileId: string, fleetId: string | undefined): Promise<UploadMileageResponse | null> => {
  if (!fileId || !fleetId) return null;

  const apiType: MaestroAPI = {
    method: 'POST',
    endpoint: `cp-be/file-uploads/${fleetId}/confirm-upload/${encodeURIComponent(fileId)}`,
    baseURL,
  };

  const res = await maestroApiCall<UploadMileageResponse>(apiType);
  return res as UploadMileageResponse;
};

export function useUploadMileage({ fleetId }: { fleetId: string | undefined }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<UploadMileageResponse | null, Error, string>({
    mutationFn: (fileId: string) => uploadMileage(fileId, fleetId),
    onError: () => {
      enqueueSnackbar(t('Upload failed. Please ensure file size is under 60KB.'), { variant: 'error' });
    },
  });
}
